import api, { getAxiosBaseUrl } from "./axios";
import { notifyServiceUnavailable } from "../utils/serviceAvailability";

export const API_TIMEOUTS = Object.freeze({
  read: 60000,
  mutation: 120000,
  upload: 300000,
});

const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);
const TRANSIENT_STATUSES = new Set([408, 425, 429, 502, 503, 504]);
const TRANSIENT_CODES = new Set(["ECONNABORTED", "ETIMEDOUT", "ERR_NETWORK"]);

export class ApiError extends Error {
  constructor(message, status, data, request = {}, transport = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
    this.code = data?.code || transport?.code || "";
    this.details = data?.details || null;
    this.requestId = data?.requestId || "";
    this.method = String(request?.method || "").toUpperCase();
    this.path = String(request?.path || "");
    this.isTimeout = transport?.isTimeout === true;
    this.isNetworkError = transport?.isNetworkError === true;
    this.isOffline = transport?.isOffline === true;
    this.isTransportError = transport?.isTransportError === true;
    this.retryable = transport?.retryable === true;
  }
}

function browserOffline() {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

function axiosTransportMeta(error) {
  const status = Number(error?.response?.status || 0);
  const code = String(error?.code || "").toUpperCase();
  const message = String(error?.message || "").toLowerCase();
  const isTimeout =
    code === "ECONNABORTED" ||
    code === "ETIMEDOUT" ||
    message.includes("timeout") ||
    message.includes("timed out");
  const isNetworkError =
    !error?.response &&
    (code === "ERR_NETWORK" ||
      message.includes("network error") ||
      message.includes("failed to fetch") ||
      message.includes("load failed"));
  const isOffline = isNetworkError && browserOffline();
  const retryable =
    TRANSIENT_STATUSES.has(status) ||
    TRANSIENT_CODES.has(code) ||
    isTimeout ||
    isNetworkError;

  return {
    code,
    isTimeout,
    isNetworkError,
    isOffline,
    isTransportError: isTimeout || isNetworkError || TRANSIENT_STATUSES.has(status),
    retryable,
  };
}

function normalizeAxiosError(error) {
  const status = error?.response?.status || 0;
  const data = error?.response?.data || null;
  const method = String(error?.config?.method || "").toUpperCase();
  const path = String(error?.config?.url || "");
  const transport = axiosTransportMeta(error);

  let message = data?.message || data?.error || error?.message || "Error de conexión con el servidor";
  if (!data?.message && !data?.error) {
    if (transport.isOffline) {
      message = "Este dispositivo está sin conexión a Internet. Tus datos siguen acá para que puedas reintentar cuando vuelva la red.";
    } else if (transport.isTimeout) {
      message = SAFE_METHODS.has(method)
        ? "El servidor tardó más de lo esperado en responder. WebProfit puede reintentar la consulta sin duplicar información."
        : "La operación tardó más de lo esperado y no recibimos una confirmación final del servidor. No significa necesariamente que haya fallado.";
    } else if (transport.isNetworkError) {
      message = "Se interrumpió la comunicación con el servidor. Tus datos siguen disponibles y no asumimos que la operación haya fallado.";
    }
  }

  return new ApiError(message, status, data, { method, path }, transport);
}

function apiBasePath() {
  const rawBase = String(getAxiosBaseUrl() || "").trim();
  if (!rawBase) return "";

  try {
    const parsed = new URL(
      rawBase,
      typeof window !== "undefined" ? window.location.origin : "http://localhost",
    );
    return parsed.pathname.replace(/\/+$/, "") || "/";
  } catch {
    return rawBase
      .replace(/[?#].*$/, "")
      .replace(/^https?:\/\/[^/]+/i, "")
      .replace(/\/+$/, "") || "/";
  }
}

export function resolveApiContractPath(contractPath) {
  const normalized = `/${String(contractPath || "").trim().replace(/^\/+/, "")}`;
  if (!normalized.startsWith("/api/") && normalized !== "/api") {
    throw new TypeError(`Contrato API inválido: ${normalized}`);
  }

  // Axios combina baseURL y url incluso cuando url comienza con "/". Cuando
  // la base ya termina en /api se quita sólo ese prefijo para evitar /api/api.
  return /\/api$/i.test(apiBasePath())
    ? normalized.replace(/^\/api(?=\/|$)/i, "") || "/"
    : normalized;
}

function isFormData(value) {
  return typeof FormData !== "undefined" && value instanceof FormData;
}

function timeoutForRequest({ method, body, timeout, timeoutProfile }) {
  const explicit = Number(timeout);
  if (Number.isFinite(explicit) && explicit >= 0) return explicit;
  if (timeoutProfile && Object.prototype.hasOwnProperty.call(API_TIMEOUTS, timeoutProfile)) {
    return API_TIMEOUTS[timeoutProfile];
  }
  if (isFormData(body)) return API_TIMEOUTS.upload;
  return SAFE_METHODS.has(method) ? API_TIMEOUTS.read : API_TIMEOUTS.mutation;
}

function retryCountForRequest({ method, retryAttempts, retryMode }) {
  const explicit = Number(retryAttempts);
  if (Number.isFinite(explicit) && explicit >= 0) return Math.min(3, Math.floor(explicit));
  if (retryMode === "idempotent") return 1;
  return SAFE_METHODS.has(method) ? 2 : 0;
}

function retryDelay(attempt, retryDelayMs) {
  const base = Math.max(150, Number(retryDelayMs || 500));
  return Math.min(3000, base * (2 ** attempt));
}

function wait(ms, signal) {
  if (!ms) return Promise.resolve();
  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (callback) => {
      if (settled) return;
      settled = true;
      if (signal) signal.removeEventListener("abort", onAbort);
      callback();
    };
    const timer = setTimeout(() => finish(resolve), ms);
    const onAbort = () => {
      clearTimeout(timer);
      const error = new Error("Solicitud cancelada");
      error.code = "ERR_CANCELED";
      finish(() => reject(error));
    };
    if (!signal) return;
    if (signal.aborted) {
      onAbort();
      return;
    }
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

export function isAmbiguousTransportError(error) {
  return Boolean(error?.isTimeout || error?.isNetworkError || error?.isTransportError);
}

export async function apiRequest(path, options = {}) {
  const {
    method: rawMethod = "GET",
    body,
    headers,
    params,
    signal,
    timeout,
    timeoutProfile,
    retryAttempts,
    retryDelayMs,
    retryMode,
    ...restOptions
  } = options;

  const method = String(rawMethod || "GET").toUpperCase();
  const maxRetries = retryCountForRequest({ method, retryAttempts, retryMode });
  const requestTimeout = timeoutForRequest({ method, body, timeout, timeoutProfile });
  let lastAxiosError = null;

  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    try {
      const response = await api.request({
        url: path,
        method,
        data: body,
        headers,
        params,
        signal,
        timeout: requestTimeout,
        __webprofitManagedRequest: true,
        ...restOptions,
      });

      return response.data;
    } catch (error) {
      lastAxiosError = error;
      const transport = axiosTransportMeta(error);
      const canceled = error?.code === "ERR_CANCELED" || signal?.aborted;
      const canRetry = !canceled && transport.retryable && attempt < maxRetries;
      if (canRetry) {
        await wait(retryDelay(attempt, retryDelayMs), signal);
        continue;
      }
      break;
    }
  }

  // Una mutación fallida no debe desmontar el formulario mediante el boundary global:
  // se conserva el estado local y se informa allí. Las lecturas sí pueden escalar
  // a la pantalla de reconexión cuando agotaron sus reintentos.
  if (lastAxiosError && SAFE_METHODS.has(method)) notifyServiceUnavailable(lastAxiosError);
  const normalizedError = normalizeAxiosError(lastAxiosError || new Error("Error de conexión con el servidor"));

  if (
    normalizedError.status === 402 &&
    normalizedError.data?.code === "BILLING_RESTRICTED" &&
    typeof window !== "undefined"
  ) {
    window.dispatchEvent(
      new CustomEvent("webprofit:billing-restricted", {
        detail: {
          path,
          access: normalizedError.data?.access || {},
        },
      }),
    );
  }

  throw normalizedError;
}

export function apiContractRequest(contractPath, options = {}) {
  return apiRequest(resolveApiContractPath(contractPath), options);
}

export async function apiRequestFirst(paths, options = {}) {
  const list = Array.isArray(paths) ? paths : [paths];
  let lastError = null;

  for (const path of list) {
    try {
      return await apiRequest(path, options);
    } catch (error) {
      lastError = error;

      // Solo prueba la siguiente ruta cuando la actual no existe.
      if (error?.status !== 404) {
        throw error;
      }
    }
  }

  throw lastError || new ApiError("Ruta no encontrada", 404, null);
}

export function formatApiError(error, fallback = "No se pudo completar la operación") {
  const baseMessage = String(error?.message || fallback).trim() || fallback;
  const fieldMessages = Array.isArray(error?.details?.fields)
    ? error.details.fields
        .slice(0, 3)
        .map((field) => String(field?.message || "").trim())
        .filter(Boolean)
    : [];
  const detail = fieldMessages.length ? ` ${fieldMessages.join(" ")}` : "";
  const code = String(error?.code || "").trim();
  const requestId = String(error?.requestId || "").trim();
  const references = [
    code && code !== "NOT_FOUND" && !["ECONNABORTED", "ETIMEDOUT", "ERR_NETWORK"].includes(code) ? code : "",
    requestId ? `solicitud ${requestId}` : "",
  ].filter(Boolean);

  return `${baseMessage}${detail}${references.length ? ` (${references.join(" · ")})` : ""}`;
}

export function getApiBaseUrl() {
  return getAxiosBaseUrl();
}
